import React from 'react';
import {Box, Typography, Link as MuiLink} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {t} from '../../themes/statamicTokens';
import StatusDot from './StatusDot';

const crumbLinkSx = {
    color: t.color.text.secondary,
    fontSize: `${t.type.small.size}px`,
    textDecoration: 'none',
    '&:hover': { color: t.color.text.primary, textDecoration: 'underline' },
};

const BackCrumb = ({to, label}) => (
    <MuiLink
        component={RouterLink}
        to={to}
        underline="none"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mb: 0.5,
            ...crumbLinkSx,
        }}
    >
        <ChevronLeft fontSize="inherit" sx={{mr: 0.25}}/>
        {label}
    </MuiLink>
);

const Trail = ({items}) => (
    <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 0.5}}>
        {items.map((item, i) => {
            const isLast = i === items.length - 1;
            return (
                <Box key={`${item.label}-${i}`} sx={{display: 'inline-flex', alignItems: 'center'}}>
                    {item.to && !isLast ? (
                        <MuiLink component={RouterLink} to={item.to} underline="none" sx={crumbLinkSx}>
                            {item.label}
                        </MuiLink>
                    ) : (
                        <Typography
                            component="span"
                            sx={{
                                ...crumbLinkSx,
                                color: isLast ? t.color.text.primary : t.color.text.secondary,
                                fontWeight: isLast ? 500 : 400,
                            }}
                        >
                            {item.label}
                        </Typography>
                    )}
                    {!isLast && (
                        <ChevronRight sx={{fontSize: 14, color: t.color.text.muted, mx: 0.5}}/>
                    )}
                </Box>
            );
        })}
    </Box>
);

const PageHeader = ({
    title,
    subtitle,
    statusVariant,
    breadcrumb,
    actions,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            pt: 3,
            pb: 2,
        }}
    >
        <Box sx={{minWidth: 0}}>
            {Array.isArray(breadcrumb) && breadcrumb.length > 0 && <Trail items={breadcrumb}/>}
            {breadcrumb && !Array.isArray(breadcrumb) && <BackCrumb {...breadcrumb}/>}

            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                {statusVariant && <StatusDot variant={statusVariant}/>}
                <Typography
                    sx={{
                        fontSize: `${t.type.display.size}px`,
                        fontWeight: t.type.display.weight,
                        lineHeight: t.type.display.lineHeight,
                        letterSpacing: t.type.display.tracking,
                        color: t.color.text.primary,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            {subtitle && (
                <Typography
                    sx={{
                        mt: 0.5,
                        fontSize: `${t.type.small.size}px`,
                        color: t.color.text.secondary,
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>

        {actions && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
                {actions}
            </Box>
        )}
    </Box>
);

export default PageHeader;
